import keycloak from "../keycloak";
import { postData } from "../api/apiService";

const cache = new Map();
/** Short-lived cache for AI summary markdown (path → content). */
const textCache = new Map();

function currentSub() {
  return keycloak.tokenParsed?.sub ?? "";
}

function cacheKey(path, permissions, durationMinutes) {
  return `${currentSub()}::${permissions}::${durationMinutes}::${path}`;
}

function textCacheKey(path) {
  return `${currentSub()}::${path ?? ""}`;
}

function isFresh(entry) {
  if (!entry?.expiresAtMs) return false;
  // Refresh 60s before expiry
  return entry.expiresAtMs - 60000 > Date.now();
}

function requireSignedIn(message) {
  if (!keycloak.token) {
    throw new Error(message);
  }
}

/**
 * Request a short-lived Azure SAS URL from DMQT API (Token Factory backed).
 * @param {string} path - Catalog path (rbfs/... or ms2/...), or "" / "/" for container list
 * @param {{ permissions?: string, durationMinutes?: number }} [options]
 * @returns {Promise<string>} Full sas_url including query string
 */
export async function getSasUrl(
  path,
  { permissions = "r", durationMinutes = 60 } = {},
) {
  requireSignedIn("You must be signed in to download or list storage files");

  const key = cacheKey(path, permissions, durationMinutes);
  const cached = cache.get(key);
  if (cached && isFresh(cached)) {
    return cached.sasUrl;
  }

  const headers = { Authorization: `Bearer ${keycloak.token}` };
  const data = await postData(
    "/storage/sas-url",
    {
      path: path ?? "",
      permissions,
      duration_minutes: durationMinutes,
    },
    headers,
  );

  if (!data?.sas_url) {
    throw new Error("SAS URL response missing sas_url");
  }

  let expiresAtMs = Date.now() + durationMinutes * 60000;
  if (data.expires_at) {
    const parsed = Date.parse(data.expires_at);
    if (!Number.isNaN(parsed)) {
      expiresAtMs = parsed;
    }
  }

  cache.set(key, { sasUrl: data.sas_url, expiresAtMs });
  return data.sas_url;
}

/**
 * Fetch blob text via DMQT API (server mints SAS and reads Azure).
 * Avoids browser→Azure CORS blocks that show as Network 200 but fail in JS.
 * @param {string} path - Catalog path to a .md / .txt blob
 * @param {{ durationMinutes?: number }} [options]
 * @returns {Promise<{ content: string, path: string }>}
 */
export async function fetchBlobText(path, { durationMinutes = 15 } = {}) {
  requireSignedIn("You must be signed in to read storage files");

  const key = textCacheKey(path);
  const cached = textCache.get(key);
  if (cached && isFresh(cached)) {
    return { content: cached.content, path: cached.path ?? path ?? "" };
  }

  const headers = { Authorization: `Bearer ${keycloak.token}` };
  const data = await postData(
    "/storage/blob-text",
    {
      path: path ?? "",
      duration_minutes: durationMinutes,
    },
    headers,
  );
  if (typeof data?.content !== "string") {
    throw new TypeError("Blob text response missing content");
  }
  const result = { content: data.content, path: data.path ?? path ?? "" };
  textCache.set(key, {
    content: result.content,
    path: result.path,
    expiresAtMs: Date.now() + durationMinutes * 60000,
  });
  return result;
}

/** Clear in-memory SAS / blob-text caches (logout / session loss / tests). */
export function clearSasUrlCache() {
  cache.clear();
  textCache.clear();
}
